import { useRef, useState } from 'react'
import { PostAttachment, getAttachmentViewUrl } from '../../api/posts'

interface Props {
  value: string
  onChange: (value: string) => void
  attachments: PostAttachment[]
  onFilesPicked: (files: File[]) => void
  isUploading: boolean
}

export default function PostMarkdownEditor({ value, onChange, attachments, onFilesPicked, isUploading }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const applyWrap = (prefix: string, suffix = prefix) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart ?? 0
    const end = ta.selectionEnd ?? 0
    const selected = value.slice(start, end)
    const nextValue = value.slice(0, start) + prefix + selected + suffix + value.slice(end)
    onChange(nextValue)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = start + prefix.length
      ta.selectionEnd = start + prefix.length + selected.length
    })
  }

  const insertAtCursor = (text: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart ?? 0
    const end = ta.selectionEnd ?? 0
    const nextValue = value.slice(0, start) + text + value.slice(end)
    onChange(nextValue)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = start + text.length
      ta.selectionEnd = start + text.length
    })
  }

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    onFilesPicked(Array.from(files))
  }

  return (
    <div
      className={`border rounded-lg p-3 bg-white ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
      onDragOver={(e) => {
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setIsDragging(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setIsDragging(false)
        handleFiles(e.dataTransfer.files)
      }}
    >
      <div className="flex flex-wrap gap-2 mb-2">
        <button type="button" className="px-2 py-1 text-sm rounded border" onClick={() => applyWrap('**')}>Жирный</button>
        <button type="button" className="px-2 py-1 text-sm rounded border" onClick={() => applyWrap('_')}>Курсив</button>
        <button type="button" className="px-2 py-1 text-sm rounded border" onClick={() => insertAtCursor('\n- ')}>Список</button>
        <button type="button" className="px-2 py-1 text-sm rounded border" onClick={() => insertAtCursor('\n## ')}>H2</button>
        <label className="px-2 py-1 text-sm rounded border cursor-pointer hover:bg-gray-50">
          {isUploading ? 'Загрузка...' : 'Прикрепить файлы'}
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files)
              e.target.value = ''
            }}
            disabled={isUploading}
          />
        </label>
      </div>

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={10}
        className="w-full border rounded p-2"
        placeholder="Пишите текст в Markdown. Можно перетащить файлы прямо сюда."
      />

      {attachments.length > 0 && (
        <div className="mt-3">
          <div className="text-sm text-gray-700 mb-1">Файлы (клик вставляет ссылку в курсор):</div>
          <div className="flex flex-wrap gap-2">
            {attachments.map((file) => (
              <button
                key={file.id}
                type="button"
                className="text-sm px-2 py-1 rounded bg-gray-100 border hover:bg-blue-50"
                onClick={() => insertAtCursor(`[${file.original_filename}](${getAttachmentViewUrl(file.id)})`)}
              >
                {file.original_filename}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
